import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Input, Typography } from 'antd';
import {
  LikeOutlined,
  DownloadOutlined,
  FileAddOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { AppBarCMS } from '../../../../basic';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { CollapseBlock, QuestionModal, Spinner } from '../../../../common';
import TableFilter from '../../../../common/TableFilter/TableFilter.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { useSelector } from 'react-redux';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';
import usePhdThesisPermissions from './usePhdThesisPermissions.jsx';
import {
  useApprovePhdThesisMutation,
  useArchivePhdThesisMutation,
  useDeletePhdCommentMutation,
  useDeletePhdThesisMutation,
  useBulkApprovePhdThesesMutation,
  useDenyPhdThesisMutation,
  useDuplicatePhdThesisMutation,
  useGetPhdThesesQuery,
  useLazyGetPhdThesisDocumentQuery,
  useLazyGetPhdThesesDocumentQuery,
  useGetPhdSystemStateCurrentQuery,
  useRestorePhdThesisMutation,
  useReplacePhdThesisMutation,
  useSavePhdCommentMutation,
} from '../../../../../redux/services/phd';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import PhdThesesTable from './PhdThesesTable.jsx';
import { downloadBlobResponse } from '../../../../../utils/helpers/downloadBlobResponse';
import PhdThesisCopyModal from './PhdThesisCopyModal.jsx';
import PhdThesesDownloadModal from './PhdThesesDownloadModal.jsx';
import PhdThesisReplaceModal from './PhdThesisReplaceModal.jsx';
import PhdThesisCommentsNestedTable from './PhdThesisCommentsNestedTable.jsx';
import { getPhdApiErrorMessage } from '../utils/phdApiFeedback.js';

const TABLE_FILTER_TITLE = 'Filter tém';

const PhdThesesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pageTitle = t('Témy dizertačných prác');
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserPhdAdmin();
  const {
    isChair,
    isTeacher,
    isPhdSupervisor,
    isStudent,
    isCommissionMember,
    isPhdAdminByPermission,
    canApproveTheses,
    canSeePendingFilter,
    shouldScopePendingCount,
    isChairOnly,
    isChairTeacher,
    restrictStatusFilters,
    canEditTheses,
    canCreateTheses,
    canDeleteTheses,
    canViewTheses,
    canCreateComment,
    canEditPendingTheses,
    canArchiveRestore,
    currentUserPersonId,
    isOwnSupervisor,
    backendFlag,
    canDeleteRow,
    canEditRow,
    canApproveRow,
    canArchiveRow,
    canDefendRow,
    canRestoreRow,
    canDenyRow,
    editDisabledTooltip,
    canCopyRow,
    canCreateCommentRow,
  } = usePhdThesisPermissions(user, isAdmin, t);
  const [statusFilter, setStatusFilter] = useState(
    localStorage.getItem(TABLE_FILTER_TITLE) || 'active',
  );
  const [pagination, setPagination] = useState({ page: 1, per_page: 25 });
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [copyTarget, setCopyTarget] = useState(null);
  const [replacementTarget, setReplacementTarget] = useState(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadSchoolYearId, setDownloadSchoolYearId] = useState(null);
  const [actionModal, setActionModal] = useState({ type: null, row: null });
  const [expandedCommentRowKeys, setExpandedCommentRowKeys] = useState([]);

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [statusFilter]);

  useEffect(() => {
    if (!restrictStatusFilters) {
      return;
    }
    if (statusFilter !== 'active') {
      setStatusFilter('active');
    }
  }, [restrictStatusFilters, statusFilter]);

  useEffect(() => {
    const allowed = canSeePendingFilter
      ? ['mine', 'active', 'pending', 'archived']
      : ['mine', 'active', 'archived'];
    if (!allowed.includes(statusFilter)) {
      setStatusFilter('active');
    }
  }, [canSeePendingFilter, statusFilter]);

  const [deleteThesis] = useDeletePhdThesisMutation();
  const [deleteComment] = useDeletePhdCommentMutation();
  const [approveThesis] = useApprovePhdThesisMutation();
  const [bulkApproveTheses, { isLoading: isBulkApproving }] =
    useBulkApprovePhdThesesMutation();
  const [denyThesis] = useDenyPhdThesisMutation();
  const [archiveThesis] = useArchivePhdThesisMutation();
  const [restoreThesis] = useRestorePhdThesisMutation();
  const [downloadThesisDoc, { isLoading: isDownloading }] =
    useLazyGetPhdThesisDocumentQuery();
  const [downloadThesesDoc, { isLoading: isDownloadingAll }] =
    useLazyGetPhdThesesDocumentQuery();
  const [duplicateThesis, { isLoading: isCopying }] =
    useDuplicatePhdThesisMutation();
  const [replaceThesis, { isLoading: isReplacing }] =
    useReplacePhdThesisMutation();
  const [saveComment] = useSavePhdCommentMutation();
  const { deleteSuccess, deleteFailed, saveSuccess, saveFailed, errorMessage } =
    useToastMessages();

  const {
    examinerMap,
    studyFieldMap,
    studyFieldCodeMap,
    schoolYears,
    schoolYearOptions,
    schoolYearMap,
    ensureSchoolYearSelection,
    isCreatingSchoolYear,
    isLoading: isReferenceLoading,
  } = usePhdReferenceData({
    includeExaminers: true,
    includeStudents: false,
  });

  const currentSchoolYearId = useMemo(() => {
    const now = dayjs();
    const academicStartYear = now.month() >= 8 ? now.year() : now.year() - 1;

    const byAcademicStart = (schoolYears || []).find(
      (year) => dayjs(year?.start_year).year() === academicStartYear,
    );
    if (byAcademicStart?.id) {
      return byAcademicStart.id;
    }

    const byDateRange = (schoolYears || []).find((year) => {
      const start = dayjs(year?.start_year).startOf('day');
      const end = dayjs(year?.end_year).endOf('day');
      return (
        start.isValid() &&
        end.isValid() &&
        now.isBetween(start, end, null, '[]')
      );
    });

    return byDateRange?.id ?? null;
  }, [schoolYears]);

  useEffect(() => {
    if (!isDownloadModalOpen) {
      setDownloadSchoolYearId(currentSchoolYearId || null);
    }
  }, [currentSchoolYearId, isDownloadModalOpen]);

  const thesisQueryParams = useMemo(() => {
    const params = {
      paginated: 1,
      page: pagination.page,
      per_page: pagination.per_page,
    };
    if (searchTerm) {
      params.search = searchTerm;
    }
    if (restrictStatusFilters) {
      params.status = 'approved';
    }
    if (statusFilter === 'active') {
      params.status = 'approved';
      params.archive_status = 'active';
    }
    if (statusFilter === 'pending') {
      params.status = 'pending';
    }
    if (statusFilter === 'mine' && currentUserPersonId) {
      params.supervisor_id = currentUserPersonId;
      params.archive_status = 'active';
    }
    if (statusFilter === 'archived') {
      params.archive_status = 'archived';
    }

    return Object.keys(params).length ? params : undefined;
  }, [
    restrictStatusFilters,
    statusFilter,
    currentUserPersonId,
    pagination.page,
    pagination.per_page,
    searchTerm,
  ]);

  const pendingCountParams = useMemo(() => {
    if (!shouldScopePendingCount) {
      return undefined;
    }
    const params = { status: 'pending', paginated: 1, per_page: 1 };
    return params;
  }, [shouldScopePendingCount]);

  const {
    data: theses,
    isLoading,
    error: loadError,
    refetch,
  } = useGetPhdThesesQuery(thesisQueryParams, {
    refetchOnMountOrArgChange: true,
  });
  const { data: systemState, isLoading: isStateLoading } =
    useGetPhdSystemStateCurrentQuery();
  const { data: pendingThesesForCount } = useGetPhdThesesQuery(
    pendingCountParams,
    {
      skip: !shouldScopePendingCount,
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: replacementCandidates, isFetching: isLoadingCandidates } =
    useGetPhdThesesQuery(
      replacementTarget
        ? {
            status: 'approved',
            archive_status: 'active',
            availability: 'available',
            phd_study_field_id: replacementTarget.phd_study_field_id,
            paginated: 1,
            per_page: 100,
          }
        : undefined,
      {
        skip: !replacementTarget,
        refetchOnMountOrArgChange: true,
      },
    );
  const isSystemClosed = systemState?.is_open === false;

  const dash = '—';
  const resolveSupervisor = (thesis) =>
    thesis?.supervisor_display_name ||
    examinerMap.get(thesis.phd_supervisor_id) ||
    thesis?.supervisor?.full_name ||
    dash;
  const resolveSpecialist = (thesis) =>
    examinerMap.get(thesis.phd_supervisor_specialist_id) ||
    thesis?.supervisor_specialist?.full_name ||
    dash;
  const resolveStudyField = (thesis) => {
    const studyProgram = thesis?.presentation?.studyProgram;
    const studyField = thesis?.presentation?.studyField;
    if (studyProgram && studyField) {
      return studyProgram === studyField
        ? studyProgram
        : `${studyProgram} - ${studyField}`;
    }
    if (studyProgram || studyField) {
      return studyProgram || studyField;
    }
    const mapped = studyFieldMap.get(thesis.phd_study_field_id);
    if (mapped) {
      return mapped;
    }
    const fallback = thesis?.studyField || thesis?.study_field;
    if (!fallback) {
      return dash;
    }
    if (typeof fallback === 'string') {
      return fallback;
    }
    return fallback.focus
      ? `${fallback.study_field} - ${fallback.focus}`
      : fallback.study_field || dash;
  };
  const resolveStudyProgramCode = (thesis) => {
    const liveCode =
      thesis?.studyField?.code ||
      thesis?.study_field?.code ||
      studyFieldCodeMap.get(thesis.phd_study_field_id);

    return liveCode || resolveStudyField(thesis);
  };

  const handleDelete = async (row) => {
    try {
      await deleteThesis(row.id).unwrap();
      deleteSuccess();
      refetch();
    } catch (error) {
      deleteFailed(error);
    }
  };

  const handleApprove = async (row) => {
    try {
      await approveThesis(row.id).unwrap();
      saveSuccess();
      refetch();
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleBulkApprove = async () => {
    const idsToApprove = selectedRowKeys.length
      ? selectedRowKeys
      : approvablePendingRowKeys;

    if (!idsToApprove.length) {
      return;
    }

    try {
      const summary = await bulkApproveTheses(idsToApprove).unwrap();
      const unresolvedFailedCount =
        summary?.failed?.filter(
          (item) => item?.reason === 'unresolved_comments',
        )?.length || 0;
      if (summary?.failed?.length) {
        errorMessage({
          message: t('Niektoré témy sa nepodarilo schváliť'),
          description:
            unresolvedFailedCount > 0
              ? t(
                  'Schválené: {{approved}}, neúspešné: {{failed}}. Neúspešné pre nevyriešené komentáre: {{unresolved}}.',
                  {
                    approved: summary?.approved || 0,
                    failed: summary?.failed?.length || 0,
                    unresolved: unresolvedFailedCount,
                  },
                )
              : t('Schválené: {{approved}}, neúspešné: {{failed}}', {
                  approved: summary?.approved || 0,
                  failed: summary?.failed?.length || 0,
                }),
        });
      } else {
        saveSuccess();
      }
      setSelectedRowKeys([]);
      refetch();
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleArchive = async (row) => {
    try {
      await archiveThesis(row.id).unwrap();
      saveSuccess();
      refetch();
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleDeny = async (row) => {
    try {
      await denyThesis(row.id).unwrap();
      saveSuccess();
      refetch();
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleRestore = async (row) => {
    try {
      await restoreThesis(row.id).unwrap();
      saveSuccess();
      refetch();
    } catch (error) {
      saveFailed(error);
    }
  };

  const openActionModal = (type, row) => {
    setActionModal({ type, row });
  };

  const closeActionModal = () => {
    setActionModal({ type: null, row: null });
  };

  const handleConfirmAction = async () => {
    if (!actionModal?.type) {
      return;
    }

    const row = actionModal.row;
    const type = actionModal.type;

    try {
      if (type === 'approve') {
        await handleApprove(row);
      } else if (type === 'deny') {
        await handleDeny(row);
      } else if (type === 'archive') {
        await handleArchive(row);
      } else if (type === 'restore') {
        await handleRestore(row);
      } else if (type === 'bulkApprove') {
        await handleBulkApprove();
      }
    } finally {
      closeActionModal();
    }
  };

  const handleDownload = async (row) => {
    try {
      const response = await downloadThesisDoc(row.id).unwrap();
      downloadBlobResponse(
        response,
        `${row.title_sk || row.title_en || t('Dizertačná téma')}.docx`,
      );
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleDownloadAll = async (schoolYearId) => {
    if (!schoolYearId) {
      errorMessage({ message: t('Nie je dostupný školský rok pre export.') });
      return;
    }

    try {
      const response = await downloadThesesDoc(schoolYearId).unwrap();
      const label = schoolYearMap.get(schoolYearId);
      const suffix = label ? `_${label.replace('/', '-')}` : '';
      downloadBlobResponse(response, `dizertacne_temy${suffix}.docx`);
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleOpenDownloadModal = () => {
    setDownloadSchoolYearId(currentSchoolYearId || null);
    setIsDownloadModalOpen(true);
  };

  const handleCloseDownloadModal = () => {
    setIsDownloadModalOpen(false);
  };

  const handleDownloadModalSubmit = async (schoolYearId) => {
    await handleDownloadAll(schoolYearId);
    setIsDownloadModalOpen(false);
  };

  const handleDeleteComment = async (comment) => {
    try {
      await deleteComment(comment.id).unwrap();
      deleteSuccess();
      refetch();
    } catch (error) {
      deleteFailed(error);
    }
  };

  const canResolveComment = (comment) => Boolean(comment?.is_mine || isAdmin);

  const handleToggleCommentResolved = async (comment) => {
    if (!comment?.id || !canResolveComment(comment)) {
      return;
    }

    try {
      await saveComment({
        id: comment.id,
        body: {
          is_resolved: !comment.is_resolved,
        },
      }).unwrap();
      saveSuccess();
      refetch();
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleCreateComment = (row) => {
    if (!row?.id || !canCreateComment) {
      return;
    }

    navigate(`${routes.cmsPhdCommentsCreate}?thesisId=${row.id}`);
  };

  const handleCopy = (row) => {
    setCopyTarget(row);
  };

  const handleCopySubmit = async () => {
    if (!copyTarget) {
      return;
    }

    try {
      await duplicateThesis(copyTarget.id).unwrap();
      saveSuccess();
      setCopyTarget(null);
      refetch();
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleCopyCancel = () => {
    setCopyTarget(null);
  };

  const handleReplaceSubmit = async (values) => {
    if (!replacementTarget?.id) {
      return;
    }

    try {
      await replaceThesis({
        id: replacementTarget.id,
        body: {
          replacement_thesis_id: values.replacement_thesis_id,
          effective_school_year_id: values.effective_school_year_id,
        },
      }).unwrap();
      saveSuccess();
      setReplacementTarget(null);
      refetch();
    } catch (error) {
      saveFailed(error);
    }
  };

  const replacementOptions = useMemo(
    () =>
      (replacementCandidates?.data || []).map((candidate) => ({
        value: candidate.id,
        label: `${candidate.title_sk || candidate.title_en || candidate.id} — ${
          candidate?.supervisor_display_name ||
          examinerMap.get(candidate.phd_supervisor_id) ||
          candidate?.supervisor?.full_name ||
          dash
        }`,
      })),
    [examinerMap, replacementCandidates],
  );

  const replacementSchoolYearOptions = useMemo(
    () =>
      (schoolYearOptions || []).filter(
        (option) =>
          !replacementTarget?.phd_school_year_id ||
          Number(option.value) >= Number(replacementTarget.phd_school_year_id),
      ),
    [replacementTarget?.phd_school_year_id, schoolYearOptions],
  );

  const {
    pendingCount,
    filteredTheses,
    canShowSupervisorColumn,
    canShowSpecialistColumn,
  } = useMemo(() => {
    const dataset = theses?.data || [];
    const pendingSource = shouldScopePendingCount
      ? pendingThesesForCount?.data || []
      : dataset;
    const pending = pendingSource.filter(
      (item) => !item.approved_at && !item.archived_at,
    );
    const mapped = dataset.map((thesis) => {
      const isPending = !thesis.approved_at && !thesis.archived_at;
      const statusCode = thesis.status || 'pending';
      return {
        ...thesis,
        supervisor_label: isPending ? dash : resolveSupervisor(thesis),
        specialist_label: isPending ? dash : resolveSpecialist(thesis),
        study_program_label: resolveStudyProgramCode(thesis),
        study_program_full_label: resolveStudyField(thesis),
        school_year_label: schoolYearMap.get(thesis.phd_school_year_id) || dash,
        status_code: statusCode,
        status_label: t(statusCode),
      };
    });

    let filtered = mapped;
    if (statusFilter === 'mine') {
      filtered = mapped.filter((thesis) => isOwnSupervisor(thesis));
    } else if (statusFilter === 'active') {
      filtered = mapped.filter((thesis) => thesis.status_code === 'approved');
    } else if (statusFilter === 'pending') {
      filtered = mapped.filter((thesis) => thesis.status_code === 'pending');
    } else if (statusFilter === 'archived') {
      filtered = mapped.filter((thesis) =>
        ['archived', 'denied', 'defended'].includes(thesis.status_code),
      );
    }

    const canShowSupervisorColumn = statusFilter !== 'pending';
    const canShowSpecialistColumn = statusFilter !== 'pending';

    return {
      pendingCount: shouldScopePendingCount
        ? pendingThesesForCount?.total ?? 0
        : pending.length,
      filteredTheses: filtered,
      canShowSupervisorColumn,
      canShowSpecialistColumn,
    };
  }, [
    pendingThesesForCount,
    schoolYearMap,
    statusFilter,
    shouldScopePendingCount,
    theses,
    examinerMap,
    studyFieldMap,
    studyFieldCodeMap,
    currentUserPersonId,
    t,
  ]);

  useEffect(() => {
    const validKeys = new Set(
      (filteredTheses || [])
        .filter((row) => (row?.comments_count || 0) > 0)
        .map((row) => row.id),
    );

    setExpandedCommentRowKeys((prev) =>
      prev.filter((key) => validKeys.has(key)),
    );
  }, [filteredTheses]);

  const approvablePendingRowKeys = (filteredTheses || [])
    .filter((row) => canApproveRow(row))
    .map((row) => row.id);

  const approveDisabledTooltip = (row) => {
    if ((row?.unresolved_comments_count || 0) > 0) {
      return t(
        'Schválenie nie je povolené: všetky komentáre musia byť označené ako vyriešené.',
      );
    }
    return t('Schválenie nie je povolené');
  };

  const handleStatusFilterChange = (value) => {
    setPagination((current) => ({ ...current, page: 1 }));
    setStatusFilter(value);
  };

  const handlePaginationChange = (nextPagination) => {
    setPagination({
      page: nextPagination.current,
      per_page: nextPagination.pageSize,
    });
  };

  const handleSearch = (value) => {
    setSearchTerm(value.trim());
    setPagination((current) => ({ ...current, page: 1 }));
  };

  if (isLoading || isReferenceLoading || isStateLoading) {
    return <Spinner />;
  }

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        title={getPhdApiErrorMessage(
          loadError,
          t(
            'PhD údaje sa nepodarilo načítať. Obnovte stránku alebo skúste znova.',
          ),
        )}
      />
    );
  }

  const isCreateThesisDisabled =
    isSystemClosed || (!isPhdSupervisor && !isAdmin);
  const createThesisDisabledTooltip = isSystemClosed
    ? t(
        'Systém pre dizertačné témy je momentálne zatvorený. Nové témy nie je možné vytvárať ani upravovať, kým sa znovu neotvorí.',
      )
    : !isPhdSupervisor && !isAdmin
      ? t('Dizertačnú tému môže vytvoriť iba PhD školiteľ.')
      : undefined;

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        permissionsBasic="phd-theses"
        mainActions={[
          {
            title: t('Pridať dizertačnú tému'),
            icon: <PlusOutlined />,
            onClick: () => navigate(routes.cmsPhdThesesCreate),
            permission: 'phd-theses.store',
            disabled: isCreateThesisDisabled,
            disabledTooltip: createThesisDisabledTooltip,
          },
          ...(isAdmin
            ? [
                {
                  title: t('Pridať obhájenú historickú prácu'),
                  icon: <FileAddOutlined />,
                  onClick: () => navigate(routes.cmsPhdThesesManualDefense),
                  permission: 'phd-theses.store',
                },
              ]
            : []),
          ...(statusFilter === 'pending' && canApproveTheses
            ? [
                {
                  title: t('Schváliť vybrané/všetky'),
                  icon: <LikeOutlined />,
                  onClick: () => openActionModal('bulkApprove', null),
                  permission: 'phd-theses.update',
                  disabled:
                    (selectedRowKeys.length === 0 &&
                      approvablePendingRowKeys.length === 0) ||
                    isBulkApproving,
                },
              ]
            : []),
          ...(isAdmin && statusFilter === 'active'
            ? [
                {
                  title: t('Stiahnuť zoznam diz. tém'),
                  icon: <DownloadOutlined />,
                  onClick: handleOpenDownloadModal,
                  permission: 'phd-theses.index',
                  disabled: isDownloadingAll,
                },
              ]
            : []),
        ]}
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako prebieha schvaľovanie dizertačných tém?')}
          description={t(
            'Školiteľ alebo administrátor doktorandského štúdia vytvorí tému, pracovná skupina ju posúdi a schválená aktívna téma sa môže priradiť doktorandovi. Obhajoba alebo náhrada ukončí jej účinnosť.',
          )}
        />
        {!restrictStatusFilters && (
          <CollapseBlock>
            <TableFilter
              title={t(TABLE_FILTER_TITLE)}
              value={statusFilter}
              setValue={handleStatusFilterChange}
              options={[
                { value: 'mine', label: t('Moje témy') },
                { value: 'active', label: t('Aktívne témy') },
                ...(canSeePendingFilter
                  ? [
                      {
                        value: 'pending',
                        label: t('Čakajúce na schválenie'),
                        count: pendingCount,
                      },
                    ]
                  : []),
                { value: 'archived', label: t('Archív') },
              ]}
            />
          </CollapseBlock>
        )}
        {statusFilter === 'archived' && (
          <Typography.Text type="secondary">
            {t(
              'Archív obsahuje uzavreté alebo historické záznamy. Sú iba na čítanie; dostupné zostáva prezeranie, tlač a povolený export.',
            )}
          </Typography.Text>
        )}

        <CollapseBlock>
          <Input.Search
            placeholder={t('Hľadať dizertačnú tému')}
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              if (!event.target.value) {
                handleSearch('');
              }
            }}
            onSearch={handleSearch}
            allowClear
            style={{ maxWidth: 360, marginBottom: 16 }}
          />
          <PhdThesesTable
            theses={filteredTheses}
            status={statusFilter}
            showSupervisorColumn={canShowSupervisorColumn}
            showSpecialistColumn={canShowSpecialistColumn}
            rowSelection={
              statusFilter === 'pending' && canApproveTheses
                ? {
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                    getCheckboxProps: (record) => ({
                      disabled: !canApproveRow(record),
                    }),
                  }
                : undefined
            }
            canEditRow={canEditRow}
            canApproveRow={canApproveRow}
            approveDisabledTooltip={approveDisabledTooltip}
            canDenyRow={canDenyRow}
            onApprove={(row) => openActionModal('approve', row)}
            onDefend={(row) =>
              navigate(`${routes.cmsPhdThesesDefend}/${row.id}`)
            }
            onDeny={(row) => openActionModal('deny', row)}
            onArchive={(row) => openActionModal('archive', row)}
            onRestore={(row) => openActionModal('restore', row)}
            canDefendRow={canDefendRow}
            canArchiveRow={canArchiveRow}
            canRestoreRow={canRestoreRow}
            onDetailClick={(row) =>
              navigate(`${routes.cmsPhdThesesEdit}/${row.id}`)
            }
            onDelete={canDeleteTheses ? handleDelete : undefined}
            canDeleteRow={canDeleteRow}
            onCopy={handleCopy}
            canCopyRow={canCopyRow}
            onReplace={isAdmin ? setReplacementTarget : undefined}
            canReplaceRow={(row) =>
              Boolean(isAdmin && row?.can_replace !== false && row?.is_current)
            }
            onCreateComment={handleCreateComment}
            canCreateCommentRow={canCreateCommentRow}
            expandedRowKeys={expandedCommentRowKeys}
            onExpandedRowsChange={setExpandedCommentRowKeys}
            renderExpandedRow={(row) => (
              <PhdThesisCommentsNestedTable
                thesisId={row.id}
                readOnly={row?.status_code !== 'pending'}
                onEditComment={(comment) =>
                  navigate(`${routes.cmsPhdCommentsEdit}/${comment.id}`)
                }
                onDeleteComment={handleDeleteComment}
                onToggleResolved={handleToggleCommentResolved}
                canResolveComment={canResolveComment}
              />
            )}
            onDownload={handleDownload}
            onView={(row) => navigate(`${routes.cmsPhdThesesDetail}/${row.id}`)}
            onPreview={(row) =>
              navigate(`${routes.phdThesisPublicDetail}/${row.id}`)
            }
            canViewRow={() => canViewTheses}
            canDownloadRow={(row) =>
              Boolean(
                isAdmin &&
                  !isDownloading &&
                  row?.is_frozen &&
                  row?.can_download !== false,
              )
            }
            editDisabledTooltip={editDisabledTooltip}
            pagination={{
              current: theses?.current_page ?? pagination.page,
              pageSize: theses?.per_page ?? pagination.per_page,
              total: theses?.total ?? 0,
              showSizeChanger: true,
              pageSizeOptions: [10, 25, 50, 100],
            }}
            onPaginationChange={handlePaginationChange}
          />
        </CollapseBlock>
      </div>
      <PhdThesisCopyModal
        open={Boolean(copyTarget)}
        isSubmitting={isCopying}
        onCancel={handleCopyCancel}
        onSubmit={handleCopySubmit}
      />
      <PhdThesisReplaceModal
        open={Boolean(replacementTarget)}
        source={replacementTarget}
        replacementOptions={replacementOptions}
        schoolYearOptions={replacementSchoolYearOptions}
        isLoadingCandidates={isLoadingCandidates}
        isSubmitting={isReplacing}
        onCancel={() => setReplacementTarget(null)}
        onSubmit={handleReplaceSubmit}
      />
      <PhdThesesDownloadModal
        open={isDownloadModalOpen}
        defaultSchoolYearId={downloadSchoolYearId || undefined}
        schoolYearOptions={schoolYearOptions}
        onEnsureSchoolYear={ensureSchoolYearSelection}
        isCreatingSchoolYear={isCreatingSchoolYear}
        isSubmitting={isDownloadingAll}
        onCancel={handleCloseDownloadModal}
        onSubmit={handleDownloadModalSubmit}
      />
      <QuestionModal
        chosenPositive={handleConfirmAction}
        question={t(
          'Naozaj chcete schváliť túto dizertačnú tému? Téma bude schválená a komentáre budú uzavreté.',
        )}
        visible={actionModal.type === 'approve'}
        yes={t('Áno')}
        toggle={closeActionModal}
        headerTitle={t('Schváliť dizertačnú tému')}
      />
      <QuestionModal
        chosenPositive={handleConfirmAction}
        question={t(
          'Naozaj chcete zamietnuť túto dizertačnú tému? Téma sa presunie do archívu ako neschválená.',
        )}
        visible={actionModal.type === 'deny'}
        yes={t('Áno')}
        toggle={closeActionModal}
        headerTitle={t('Zamietnuť dizertačnú tému')}
      />
      <QuestionModal
        chosenPositive={handleConfirmAction}
        question={t(
          'Naozaj chcete archivovať túto dizertačnú tému? Téma nebude viac aktívna a zobrazí sa v archíve.',
        )}
        visible={actionModal.type === 'archive'}
        yes={t('Áno')}
        toggle={closeActionModal}
        headerTitle={t('Archivovať dizertačnú tému')}
      />
      <QuestionModal
        chosenPositive={handleConfirmAction}
        question={t('Naozaj chcete obnoviť túto dizertačnú tému z archívu?')}
        visible={actionModal.type === 'restore'}
        yes={t('Áno')}
        toggle={closeActionModal}
        headerTitle={t('Obnoviť dizertačnú tému')}
      />
      <QuestionModal
        chosenPositive={handleConfirmAction}
        question={t(
          'Naozaj chcete schváliť vybrané dizertačné témy? Operáciu nie je možné vrátiť späť.',
        )}
        visible={actionModal.type === 'bulkApprove'}
        yes={t('Áno')}
        toggle={closeActionModal}
        headerTitle={t('Schváliť vybrané témy')}
      />
    </>
  );
};

export default PhdThesesPage;
